import React, { createContext, useContext, useEffect, useState } from 'react';
import { Hub } from 'aws-amplify';
import Auth from '@aws-amplify/auth';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';

import {
  ACCESS_CHANGED,
  AdminAccess,
  AdminAccessState,
  AdminPermission,
  beginAccessSession,
  getAccessState,
  loadAccess,
  NO_ACCESS,
  resetAccess,
} from 'services/auth/access';

const AdminAccessContext = createContext<AdminAccess>(NO_ACCESS);

export const useAdminAccess = () => useContext(AdminAccessContext);

export const AdminAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<AdminAccessState>({ status: 'loading', access: NO_ACCESS });
  const { access, status } = state;

  useEffect(() => {
    let active = true;
    const update = () => {
      loadAccess();
    };
    const sessionChanged = () => {
      if (active) setState(getAccessState());
    };
    const authChanged = ({ payload }) => {
      if (payload.event === 'signOut') {
        resetAccess();
      } else if (payload.event === 'signIn') {
        beginAccessSession();
        update();
      } else if (payload.event === 'tokenRefresh') {
        update();
      }
    };
    window.addEventListener(ACCESS_CHANGED, sessionChanged);
    window.addEventListener('focus', update);
    Hub.listen('auth', authChanged);
    const interval = window.setInterval(update, 30000);
    beginAccessSession();
    sessionChanged();
    update();

    return () => {
      active = false;
      window.removeEventListener(ACCESS_CHANGED, sessionChanged);
      window.removeEventListener('focus', update);
      window.clearInterval(interval);
      Hub.remove('auth', authChanged);
    };
  }, []);

  const signOut = () => {
    resetAccess();
    return Auth.signOut();
  };

  if (status === 'loading') {
    return (
      <Box p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (!access.canRead) {
    return (
      <Box p={4}>
        <Typography variant="h6">
          {status === 'unavailable' ? 'Session is unavailable' : 'Admin access is not assigned'}
        </Typography>
        <Typography>
          {status === 'unavailable'
            ? 'Your session could not be loaded. Access will be checked again automatically. You can also sign out and sign in again.'
            : 'Ask an administrator to assign an admin Cognito group, then sign in again.'}
        </Typography>
        <Button onClick={signOut}>Sign out</Button>
      </Box>
    );
  }
  return (
    <AdminAccessContext.Provider value={access}>
      {/* Close stale dialogs/selections and discard cached screens when permissions change. */}
      <React.Fragment key={JSON.stringify(access)}>{children}</React.Fragment>
    </AdminAccessContext.Provider>
  );
};

export const PermissionAccess = ({
  children,
  permission,
}: {
  children: React.ReactNode;
  permission: AdminPermission;
}) => {
  const access = useAdminAccess();
  return access[permission] ? <>{children}</> : null;
};

type AccessChildrenProps = { children: React.ReactNode };

export const AdminWriteAccess = ({ children }: AccessChildrenProps) => (
  <PermissionAccess permission="canWrite">{children}</PermissionAccess>
);
export const BillingWriteAccess = ({ children }: AccessChildrenProps) => (
  <PermissionAccess permission="canManageBilling">{children}</PermissionAccess>
);
export const UserWriteAccess = ({ children }: AccessChildrenProps) => (
  <PermissionAccess permission="canManageUsers">{children}</PermissionAccess>
);
export const FraudWriteAccess = ({ children }: AccessChildrenProps) => (
  <PermissionAccess permission="canManageFraud">{children}</PermissionAccess>
);

// Metadata lets ActionField hide empty menus; the wrapper also gates every other usage.
export type AdminPermissionMetadata = { adminPermission?: AdminPermission };

type PermissionComponent<C extends React.ElementType> = React.NamedExoticComponent<
  JSX.LibraryManagedAttributes<C, React.ComponentPropsWithRef<C>>
> & { adminPermission: AdminPermission };

export const withPermissionAccess = <C extends React.ElementType>(
  Component: C,
  permission: AdminPermission
): PermissionComponent<C> => {
  const Target: React.ElementType = Component;
  const Allowed = React.forwardRef<unknown, React.ComponentPropsWithoutRef<C>>((props, ref) => (
    <PermissionAccess permission={permission}>
      <Target {...props} {...(ref ? { ref } : {})} />
    </PermissionAccess>
  ));
  return Object.assign(Allowed, { adminPermission: permission }) as PermissionComponent<C>;
};

export const withAdminWriteAccess = <C extends React.ElementType>(Component: C) =>
  withPermissionAccess(Component, 'canWrite');
export const withBillingWriteAccess = <C extends React.ElementType>(Component: C) =>
  withPermissionAccess(Component, 'canManageBilling');
export const withUserWriteAccess = <C extends React.ElementType>(Component: C) =>
  withPermissionAccess(Component, 'canManageUsers');
export const withFraudWriteAccess = <C extends React.ElementType>(Component: C) =>
  withPermissionAccess(Component, 'canManageFraud');
export const readOnlyAction = <C extends React.ElementType>(
  Component: C,
  permission: AdminPermission = 'canRead'
) => withPermissionAccess(Component, permission);
